import { useState, useMemo } from 'react';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import {
    RefreshCw,
    Search,
    Calendar,
    Check,
    XCircle,
    Pencil,
    Trash2,
    Plus,
    ExternalLink,
} from 'lucide-react';
import type { FishingEventStatusFilter, AdminFishingEventRow } from './types';
import { getCountryLabelFromIso2, iso2ToFlagEmoji } from '@/utils/countryFlagEmoji';
import {
    useFishingEventsStats,
    useAdminFishingEventsList,
    useAdminFishingEventSetStatus,
    useAdminFishingEventDelete,
    useAdminFishingEventUpdate,
    useAdminFishingEventCreate,
} from './hooks/useFishingEventsAdmin';

const EVENT_TYPES = [
    { value: 'campeonato', label: 'Campeonato' },
    { value: 'quedada', label: 'Quedada' },
    { value: 'conferencia', label: 'Conferencia' },
    { value: 'feria', label: 'Feria' },
    { value: 'otro', label: 'Otro' },
    { value: 'salida_orilla', label: 'Salida desde orilla' },
    { value: 'salida_embarcacion', label: 'Salida en embarcación' },
];

const SALIDA_INTENTS: { value: string; label: string }[] = [
    { value: 'pesca_juntos', label: 'Ir a pescar juntos' },
    { value: 'compartir_gastos_informal', label: 'También repartir gastos (entre vosotros)' },
    { value: 'ambos', label: 'Ambos' },
];

function isSalidaAdminType(t: string): boolean {
    return t === 'salida_orilla' || t === 'salida_embarcacion';
}

function toLocalInput(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function EventsPage() {
    const [tab, setTab] = useState<FishingEventStatusFilter>('pending');
    const [search, setSearch] = useState('');
    const [rejectId, setRejectId] = useState<string | null>(null);
    const [rejectReason, setRejectReason] = useState('');
    const [deleteTarget, setDeleteTarget] = useState<AdminFishingEventRow | null>(null);
    const [editRow, setEditRow] = useState<AdminFishingEventRow | null>(null);
    const [createOpen, setCreateOpen] = useState(false);

    const { data: stats } = useFishingEventsStats();
    const { data: rows = [], isLoading, refetch } = useAdminFishingEventsList(tab, search);
    const setStatus = useAdminFishingEventSetStatus();
    const del = useAdminFishingEventDelete();
    const update = useAdminFishingEventUpdate();
    const create = useAdminFishingEventCreate();

    const tabs: { value: FishingEventStatusFilter; label: string; count?: number }[] = useMemo(
        () => [
            { value: 'all', label: 'Todos', count: stats?.total },
            { value: 'pending', label: 'Pendientes', count: stats?.pending_count },
            { value: 'approved', label: 'Aprobados', count: stats?.approved_count },
            { value: 'rejected', label: 'Rechazados', count: stats?.rejected_count },
        ],
        [stats]
    );

    const handleRejectConfirm = () => {
        if (!rejectId) return;
        setStatus.mutate(
            { id: rejectId, status: 'rejected', rejectionReason: rejectReason || 'Sin motivo' },
            {
                onSuccess: () => {
                    setRejectId(null);
                    setRejectReason('');
                },
            }
        );
    };

    return (
        <div className="space-y-6">
            <PageHeader title="Eventos de pesca" description="Moderar y gestionar eventos públicos">
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => setCreateOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground"
                    >
                        <Plus size={16} />
                        Nuevo evento
                    </button>
                    <button
                        type="button"
                        onClick={() => refetch()}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground bg-muted/50"
                    >
                        <RefreshCw size={14} />
                    </button>
                </div>
            </PageHeader>

            <div className="flex flex-wrap gap-2">
                {tabs.map((t) => (
                    <button
                        key={t.value}
                        type="button"
                        onClick={() => setTab(t.value)}
                        className={cn(
                            'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                            tab === t.value
                                ? 'bg-violet-600 text-white'
                                : 'bg-muted/60 text-muted-foreground hover:bg-muted'
                        )}
                    >
                        {t.label}
                        {t.count != null ? ` (${t.count})` : ''}
                    </button>
                ))}
            </div>

            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                    type="search"
                    placeholder="Buscar por título, usuario o email…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background text-sm"
                />
            </div>

            {isLoading ? (
                <p className="text-sm text-muted-foreground">Cargando…</p>
            ) : rows.length === 0 ? (
                <p className="text-sm text-muted-foreground">No hay eventos.</p>
            ) : (
                <div className="space-y-4">
                    {rows.map((row) => (
                        <div
                            key={row.id}
                            className="rounded-xl border border-border bg-card p-4 space-y-3"
                        >
                            <div className="flex flex-wrap items-start justify-between gap-2">
                                <div>
                                    <span
                                        className={cn(
                                            'text-[10px] font-bold uppercase px-2 py-0.5 rounded-full',
                                            row.status === 'approved' && 'bg-emerald-500/20 text-emerald-400',
                                            row.status === 'pending' && 'bg-amber-500/20 text-amber-400',
                                            row.status === 'rejected' && 'bg-red-500/20 text-red-400'
                                        )}
                                    >
                                        {row.status}
                                    </span>
                                    <div className="flex items-start gap-2 gap-y-1 flex-wrap mt-1">
                                        {row.country_code && iso2ToFlagEmoji(row.country_code) ? (
                                            <span
                                                className="text-2xl leading-none shrink-0"
                                                title={getCountryLabelFromIso2(row.country_code)}
                                                aria-hidden
                                            >
                                                {iso2ToFlagEmoji(row.country_code)}
                                            </span>
                                        ) : null}
                                        <div className="min-w-0 flex-1">
                                            <h3 className="font-semibold">{row.title}</h3>
                                            {row.country_code ? (
                                                <p className="text-[11px] text-muted-foreground mt-0.5">
                                                    {getCountryLabelFromIso2(row.country_code)}
                                                </p>
                                            ) : null}
                                        </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {row.creator_username} · {row.creator_email}
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {row.status === 'pending' && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setStatus.mutate({ id: row.id, status: 'approved' })
                                                }
                                                disabled={setStatus.isPending}
                                                className="p-2 rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"
                                                title="Aprobar"
                                            >
                                                <Check size={18} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setRejectId(row.id)}
                                                className="p-2 rounded-lg bg-red-500/15 text-red-400 hover:bg-red-500/25"
                                                title="Rechazar"
                                            >
                                                <XCircle size={18} />
                                            </button>
                                        </>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setEditRow(row)}
                                        className="p-2 rounded-lg bg-muted hover:bg-muted/80"
                                        title="Editar"
                                    >
                                        <Pencil size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setDeleteTarget(row)}
                                        className="p-2 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20"
                                        title="Eliminar"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Calendar size={12} />
                                {new Date(row.starts_at).toLocaleString('es-ES')}
                                {row.ends_at ? ` → ${new Date(row.ends_at).toLocaleString('es-ES')}` : ''}
                            </p>
                            {row.location_label && (
                                <p className="text-sm text-muted-foreground">{row.location_label}</p>
                            )}
                            {row.description && (
                                <p className="text-sm line-clamp-2">{row.description}</p>
                            )}
                            {row.rejection_reason && (
                                <p className="text-xs text-red-400">Motivo: {row.rejection_reason}</p>
                            )}
                            <div className="flex gap-2 text-xs">
                                {row.external_url && (
                                    <a
                                        href={row.external_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-sky-400 flex items-center gap-1"
                                    >
                                        <ExternalLink size={12} /> Web
                                    </a>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {rejectId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
                    <div className="bg-card border border-border rounded-xl p-6 max-w-md w-full space-y-4">
                        <h3 className="font-semibold">Rechazar evento</h3>
                        <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Motivo…"
                            className="w-full min-h-[100px] rounded-lg border border-border bg-background p-3 text-sm"
                        />
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setRejectId(null);
                                    setRejectReason('');
                                }}
                                className="px-3 py-2 text-sm rounded-lg bg-muted"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleRejectConfirm}
                                className="px-3 py-2 text-sm rounded-lg bg-red-600 text-white"
                            >
                                Rechazar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deleteTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
                    <div className="bg-card border border-border rounded-xl p-6 max-w-sm w-full space-y-4">
                        <p className="text-sm">¿Eliminar «{deleteTarget.title}»?</p>
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setDeleteTarget(null)}
                                className="px-3 py-2 text-sm rounded-lg bg-muted"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={() =>
                                    del.mutate(deleteTarget.id, {
                                        onSuccess: () => setDeleteTarget(null),
                                    })
                                }
                                className="px-3 py-2 text-sm rounded-lg bg-destructive text-destructive-foreground"
                            >
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {(editRow || createOpen) && (
                <EventFormModal
                    key={createOpen ? 'new-event' : editRow?.id ?? 'x'}
                    mode={createOpen ? 'create' : 'edit'}
                    initial={createOpen ? null : editRow!}
                    onClose={() => {
                        setEditRow(null);
                        setCreateOpen(false);
                    }}
                    onSaveCreate={(payload) => {
                        create.mutate(payload, { onSuccess: () => setCreateOpen(false) });
                    }}
                    onSaveEdit={(payload) => {
                        update.mutate(payload, { onSuccess: () => setEditRow(null) });
                    }}
                    isPending={create.isPending || update.isPending}
                />
            )}
        </div>
    );
}

type CreatePayload = {
    p_title: string;
    p_description: string | null;
    p_rules_considerations: string | null;
    p_event_type: string;
    p_starts_at: string;
    p_ends_at: string | null;
    p_location_label: string | null;
    p_country_code: string | null;
    p_latitude: number | null;
    p_longitude: number | null;
    p_external_url: string | null;
    p_registration_url: string | null;
    p_registration_fee_eur: number | null;
    p_organizer_contact: string | null;
    p_cover_image_url: string | null;
    p_max_participants: number | null;
    p_salida_community_intent: string | null;
};

type EditPayload = CreatePayload & { p_id: string; p_status: string | null };

function EventFormModal({
    mode,
    initial,
    onClose,
    onSaveCreate,
    onSaveEdit,
    isPending,
}: {
    mode: 'create' | 'edit';
    initial: AdminFishingEventRow | null;
    onClose: () => void;
    onSaveCreate: (p: CreatePayload) => void;
    onSaveEdit: (p: EditPayload) => void;
    isPending: boolean;
}) {
    const [title, setTitle] = useState(initial?.title ?? '');
    const [description, setDescription] = useState(initial?.description ?? '');
    const [rulesConsiderations, setRulesConsiderations] = useState(initial?.rules_considerations ?? '');
    const [eventType, setEventType] = useState(initial?.event_type ?? 'quedada');
    const [startsLocal, setStartsLocal] = useState(toLocalInput(initial?.starts_at ?? null));
    const [endsLocal, setEndsLocal] = useState(toLocalInput(initial?.ends_at ?? null));
    const [locationLabel, setLocationLabel] = useState(initial?.location_label ?? '');
    const [countryCode, setCountryCode] = useState(initial?.country_code ?? '');
    const [lat, setLat] = useState(initial?.latitude != null ? String(initial.latitude) : '');
    const [lng, setLng] = useState(initial?.longitude != null ? String(initial.longitude) : '');
    const [externalUrl, setExternalUrl] = useState(initial?.external_url ?? '');
    const [registrationUrl, setRegistrationUrl] = useState(initial?.registration_url ?? '');
    const [registrationFeeEur, setRegistrationFeeEur] = useState(() => {
        const v = initial?.registration_fee_eur;
        if (v == null || v === '') return '';
        const n = Number(v);
        return Number.isNaN(n) ? '' : String(n);
    });
    const [organizerContact, setOrganizerContact] = useState(initial?.organizer_contact ?? '');
    const [coverUrl, setCoverUrl] = useState(initial?.cover_image_url ?? '');
    const [status, setStatus] = useState(initial?.status ?? 'approved');
    const [maxParticipants, setMaxParticipants] = useState(() => {
        const v = initial?.max_participants;
        if (v == null || v === '') return '';
        const n = Number(v);
        return Number.isNaN(n) ? '' : String(n);
    });
    const [salidaIntent, setSalidaIntent] = useState(() => {
        const i = initial?.salida_community_intent;
        if (i === 'compartir_gastos_informal' || i === 'ambos' || i === 'pesca_juntos') return i;
        return 'pesca_juntos';
    });

    const submit = () => {
        const starts_at = new Date(startsLocal).toISOString();
        const ends_at = endsLocal ? new Date(endsLocal).toISOString() : null;
        const la = lat.trim() ? parseFloat(lat) : null;
        const lo = lng.trim() ? parseFloat(lng) : null;
        const feeStr = registrationFeeEur.trim().replace(',', '.');
        let p_registration_fee_eur: number | null = null;
        if (!isSalidaAdminType(eventType) && feeStr !== '') {
            const x = parseFloat(feeStr);
            p_registration_fee_eur =
                Number.isNaN(x) || x < 0 || x > 999999.99 ? null : Math.round(x * 100) / 100;
        }
        let p_max_participants: number | null = null;
        let p_salida_community_intent: string | null = null;
        if (isSalidaAdminType(eventType)) {
            p_registration_fee_eur = null;
            p_salida_community_intent = salidaIntent;
            const mp = maxParticipants.trim();
            if (mp !== '') {
                const n = parseInt(mp, 10);
                if (!Number.isNaN(n) && n >= 2 && n <= 200) p_max_participants = n;
            }
        }
        const base = {
            p_title: title.trim(),
            p_description: description.trim() || null,
            p_rules_considerations: rulesConsiderations.trim() || null,
            p_event_type: eventType,
            p_starts_at: starts_at,
            p_ends_at: ends_at,
            p_location_label: locationLabel.trim() || null,
            p_country_code: countryCode.trim() || null,
            p_latitude: la != null && !Number.isNaN(la) ? la : null,
            p_longitude: lo != null && !Number.isNaN(lo) ? lo : null,
            p_external_url: externalUrl.trim() || null,
            p_registration_url: registrationUrl.trim() || null,
            p_registration_fee_eur,
            p_organizer_contact: organizerContact.trim() || null,
            p_cover_image_url: coverUrl.trim() || null,
            p_max_participants,
            p_salida_community_intent,
        };
        if (mode === 'create') {
            onSaveCreate(base);
        } else if (initial) {
            onSaveEdit({
                ...base,
                p_id: initial.id,
                p_status: status,
            });
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 overflow-y-auto">
            <div className="bg-card border border-border rounded-xl p-6 max-w-lg w-full my-8 space-y-3 max-h-[90vh] overflow-y-auto">
                <h3 className="font-semibold">{mode === 'create' ? 'Nuevo evento' : 'Editar evento'}</h3>
                <label className="block text-xs text-muted-foreground">Título</label>
                <input
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                />
                <label className="block text-xs text-muted-foreground">Tipo</label>
                <select
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    value={eventType}
                    onChange={(e) => {
                        const v = e.target.value;
                        setEventType(v);
                        if (!isSalidaAdminType(v)) {
                            setMaxParticipants('');
                            setSalidaIntent('pesca_juntos');
                        }
                    }}
                >
                    {EVENT_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                            {t.label}
                        </option>
                    ))}
                </select>
                {isSalidaAdminType(eventType) ? (
                    <>
                        <label className="block text-xs text-muted-foreground">Enfoque salida</label>
                        <select
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                            value={salidaIntent}
                            onChange={(e) => setSalidaIntent(e.target.value)}
                        >
                            {SALIDA_INTENTS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                        <label className="block text-xs text-muted-foreground">Máx. «Asistiré» (opcional)</label>
                        <input
                            type="number"
                            min={2}
                            max={200}
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                            value={maxParticipants}
                            onChange={(e) => setMaxParticipants(e.target.value)}
                            placeholder="Vacío = sin límite"
                        />
                    </>
                ) : null}
                <label className="block text-xs text-muted-foreground">Estado (solo edición)</label>
                <select
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    disabled={mode === 'create'}
                >
                    <option value="pending">pending</option>
                    <option value="approved">approved</option>
                    <option value="rejected">rejected</option>
                </select>
                <label className="block text-xs text-muted-foreground">Inicio</label>
                <input
                    type="datetime-local"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    value={startsLocal}
                    onChange={(e) => setStartsLocal(e.target.value)}
                />
                <label className="block text-xs text-muted-foreground">Fin</label>
                <input
                    type="datetime-local"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    value={endsLocal}
                    onChange={(e) => setEndsLocal(e.target.value)}
                />
                <label className="block text-xs text-muted-foreground">Ubicación</label>
                <input
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    value={locationLabel}
                    onChange={(e) => setLocationLabel(e.target.value)}
                />
                <label className="block text-xs text-muted-foreground">País (ISO)</label>
                <input
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    placeholder="ES"
                />
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="block text-xs text-muted-foreground">Lat</label>
                        <input
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                            value={lat}
                            onChange={(e) => setLat(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-muted-foreground">Lng</label>
                        <input
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                            value={lng}
                            onChange={(e) => setLng(e.target.value)}
                        />
                    </div>
                </div>
                <label className="block text-xs text-muted-foreground">Descripción</label>
                <textarea
                    className="w-full min-h-[80px] rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
                <label className="block text-xs text-muted-foreground">Consideraciones / reglas</label>
                <textarea
                    className="w-full min-h-[64px] rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    value={rulesConsiderations}
                    onChange={(e) => setRulesConsiderations(e.target.value.slice(0, 4000))}
                    placeholder="Opcional"
                />
                <label className="block text-xs text-muted-foreground">Web</label>
                <input
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    value={externalUrl}
                    onChange={(e) => setExternalUrl(e.target.value)}
                />
                <label className="block text-xs text-muted-foreground">
                    Precio inscripción (€){isSalidaAdminType(eventType) ? ' — no aplica en salidas' : ''}
                </label>
                <input
                    type="number"
                    min={0}
                    step={0.01}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm disabled:opacity-50"
                    value={registrationFeeEur}
                    onChange={(e) => setRegistrationFeeEur(e.target.value)}
                    placeholder="Vacío = sin precio; 0 = gratis"
                    disabled={isSalidaAdminType(eventType)}
                />
                <label className="block text-xs text-muted-foreground mt-1">Enlace inscripción (opcional)</label>
                <input
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    value={registrationUrl}
                    onChange={(e) => setRegistrationUrl(e.target.value)}
                    placeholder="https://…"
                />
                <label className="block text-xs text-muted-foreground">Contacto</label>
                <input
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    value={organizerContact}
                    onChange={(e) => setOrganizerContact(e.target.value)}
                />
                <label className="block text-xs text-muted-foreground">URL portada</label>
                <input
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    value={coverUrl}
                    onChange={(e) => setCoverUrl(e.target.value)}
                />
                <div className="flex justify-end gap-2 pt-4">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-muted text-sm">
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={isPending}
                        className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm"
                    >
                        Guardar
                    </button>
                </div>
            </div>
        </div>
    );
}
